import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {routerShape} from 'react-router/lib/PropTypes';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import IssueAttachmentGalleryContainer from '../issues/IssueAttachmentGalleryContainer';
import Link from '../lib/Link';
import {screenType} from '../../constants/analytics';
import {issueAttributesType, sheetObjectType} from '../../constants/proptype-validation';
import arrowBack from '../../assets/images/icons/icon-arrow-back.svg';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {hasLinkedDocument} from '../../utils/issues-helpers';
import {CurrentIssuePreviewPopupContext} from '../issues/useCurrentIssuePreviewPopup';
import IssuePreview from '../issues/IssuePreview';

const IssuePreviewPanel = ({
  issue,
  selectPushpinAction,
  deselectPushpinsAction,
  router,
  currentSheets,
  isLoadingCollection
}) => {
  const selectedIssueId = useRef();

  useEffect(() => {
    // Select the pushpin if one of the following is true -
    // 1. We didn't have the issue data before and have it now
    // 2. The current sheet has changed
    // 3. The fetching of all issues has just completed
    // In any case, we check that the fetching of all issues has been completed, we shouldn't select a pushpin before that

    if (hasLinkedDocument(issue) && !isLoadingCollection && issue.id !== selectedIssueId.current) {
      selectPushpinAction(issue);
      selectedIssueId.current = issue.id;
    }
  }, [isLoadingCollection, issue, selectPushpinAction, currentSheets, selectedIssueId]);
  useEffect(
    () => () => {
      // Deselect pushpin only when not navigating to edit page of the issue
      // This condition specify that we should only deselect when finally navigating away from preview but not when changing from 1 pushpin to another
      if (!(router?.params?.id && router.params.id === issue.id)) {
        deselectPushpinsAction();
        selectedIssueId.current = undefined;
      }
    },
    [deselectPushpinsAction, issue, router]
  );

  const ref = useRef(null);
  const [currentIssuePreviewPopup, setCurrentIssuePreviewPopup] = useState(null);

  return (
    <div className="IssuePreviewPanel" ref={ref}>
      <CurrentIssuePreviewPopupContext.Provider value={{currentIssuePreviewPopup, setCurrentIssuePreviewPopup}}>
        <IssuePreview origin={screenType.PANEL} container={ref.current} />
        <Link className="IssuePreviewPanel__back-link" to={`/${TYPE_QUALITY_ISSUES}`}>
          <SvgIcon width="11px" height="17px">
            {arrowBack}
          </SvgIcon>
        </Link>
        <IssueAttachmentGalleryContainer container={ref.current} />
      </CurrentIssuePreviewPopupContext.Provider>
    </div>
  );
};

IssuePreviewPanel.displayName = 'IssuePreviewPanel';

IssuePreviewPanel.propTypes = {
  issueId: PropTypes.string.isRequired,
  issue: issueAttributesType.isRequired,
  selectPushpinAction: PropTypes.func.isRequired,
  deselectPushpinsAction: PropTypes.func.isRequired,
  router: routerShape.isRequired,
  currentSheets: PropTypes.arrayOf(sheetObjectType),
  isLoadingCollection: PropTypes.bool.isRequired
};

export default IssuePreviewPanel;
